// Chapter 22 - Shaping
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 22.

import type { ChapterModule } from "./types";

const chapter22Module: ChapterModule = {
  meta: {
    id: "22",
    number: 22,
    title: "Shaping",
    subtitle: "Successive approximations, differential reinforcement",
    part: "Part 8 - Developing New Behavior",
    estMinutes: 70,
    bacb: ["G-7"],
  },
  curated: true,
  overview: [
    "Chapter 22 covers SHAPING: differential reinforcement of successive approximations toward a terminal behavior. The shaper (a) detects a change in the learner's environment, (b) judges whether that change is a closer approximation to the terminal behavior, and (c) differentially reinforces the closer approximation, placing all other responses on extinction.",
    "You will learn the dimensions along which behavior can be shaped (topography, rate, latency, duration, interresponse time, magnitude), the distinction between shaping across vs within response topographies, the limits of shaping, how to make shaping more efficient (SDs, physical guidance, imitative prompts, percentile schedules), clicker training as an applied technology, shaping guidelines, and the contrast between shaping (response changes; SD stays) and stimulus fading (SD changes; response stays).",
  ],
  objectives: [
    { id: "G-7", text: "Use shaping to teach new behavior via differential reinforcement of successive approximations." },
  ],
  coreConcepts: [
    {
      title: "Definition: shaping",
      body: [
        "Shaping is the differential reinforcement of successive approximations toward a terminal behavior. It is a three-part loop: detect a change in the learner's behavior, judge whether the change is a closer approximation, and differentially reinforce that approximation.",
      ],
    },
    {
      title: "Terminal behavior and target dimensions",
      body: [
        "Terminal behavior is achieved when topography, rate, latency, duration, interresponse time, or magnitude reaches a predetermined criterion. Behavior can be shaped along ANY of these measurable dimensions.",
      ],
    },
    {
      title: "Differential reinforcement and its triple effect",
      body: [
        "Differential reinforcement: reinforce only members of a response class that share a specified dimension; place all other members on extinction. Three effects: similar responses become more likely, dissimilar responses are extinguished, AND some extinction-burst responses resemble the next approximation and become reinforceable.",
      ],
    },
    {
      title: "Response differentiation and successive approximations",
      body: [
        "Consistent application of differential reinforcement produces RESPONSE DIFFERENTIATION: a new response class shaped from the previously reinforced subclass. A gradual criterion shift produces a SUCCESSION of new response classes, each closer to the terminal behavior. These are the successive approximations.",
      ],
    },
    {
      title: "Shaping across vs within response topographies",
      body: [
        "ACROSS topographies: selected members of a response class are differentially reinforced; others (different forms) are extinguished. WITHIN topography: form stays constant; differential reinforcement is applied to ANOTHER dimension (e.g., magnitude or rate).",
      ],
    },
    {
      title: "Shaping vs stimulus fading",
      body: [
        "In shaping, the antecedent stimulus STAYS THE SAME while the RESPONSE progressively becomes more differentiated. In stimulus fading (Ch 17), the RESPONSE stays the same while the antecedent stimulus changes gradually.",
      ],
    },
    {
      title: "Limits of shaping",
      body: [
        "Shaping has at least five limitations: it can be slow; it requires close observation; it relies on motivation and a sufficient existing repertoire; it can be misapplied (shaping unwanted topographies); and it may not be the most efficient tactic when prompting, modeling, or chaining could work.",
      ],
    },
    {
      title: "Increasing shaping efficiency",
      body: [
        "Use a discriminative stimulus (SD) to signal when approximations earn reinforcement; use physical guidance and imitative prompts; use a percentile schedule (reinforce responses above the Nth percentile of recent responses). Fade any prompts introduced.",
      ],
    },
    {
      title: "Clicker training",
      body: [
        "Clicker training is a science-based system for progressively shaping behavior using positive reinforcement and extinction. The clicker (a handheld device that produces a click) is a conditioned reinforcer that bridges the gap between the response and the backup reinforcer.",
      ],
    },
    {
      title: "Shaping guidelines",
      body: [
        "Assess whether shaping is the right tactic; select the terminal behavior; decide success criteria; analyze the response class; identify the first behavior to reinforce; eliminate interfering stimuli; proceed gradually; limit approximations at each level; continue reinforcement after terminal behavior is achieved; and learn to shape (practice with simple behaviors, colleagues, computer simulators).",
      ],
    },
  ],
  glossary: [
    { term: "Shaping", definition: "Differential reinforcement of successive approximations toward a terminal behavior.", sourceHint: "Shaping is defined as the differential reinforcement of successive approximations toward a terminal behavior." },
    { term: "Terminal behavior", definition: "The end product of shaping; achieved when a measurable dimension reaches a predetermined criterion.", sourceHint: "A terminal behavior - the end product of shaping - can be claimed when the topography, rate, latency, duration, interresponse time, or magnitude of the target behavior reaches a predetermined criterion level." },
    { term: "Successive approximation", definition: "Each new response class produced by a gradually changing reinforcement criterion that is closer to the terminal behavior than its predecessor.", sourceHint: "The gradually changing criterion for reinforcement during shaping results in a succession of new response classes, or successive approximations, each one closer in form to the terminal behavior." },
    { term: "Differential reinforcement", definition: "Reinforcement of members of a response class that share a specified dimension while placing all other members on extinction.", sourceHint: "Differential reinforcement refers to the act of presenting an unconditioned or conditioned reinforcer only to those emitted members of a response class that share a specified dimension or quality, while placing all other emitted response class members on extinction." },
    { term: "Response differentiation", definition: "Emergence of a new response class composed primarily of previously reinforced sub-class responses.", sourceHint: "When differential reinforcement is applied consistently within a response class, its triple effect results in a new response class composed primarily of responses sharing the characteristics of the previously reinforced subclass. This emergence of a new response class is called response differentiation." },
    { term: "Shaping across topographies", definition: "Differential reinforcement of selected members of a response class while placing other topographies on extinction.", sourceHint: "Shaping behavior across different response topographies means that selected members of a response class are differentially reinforced, whereas members of other response classes are placed on extinction." },
    { term: "Shaping within a topography", definition: "Form stays constant; differential reinforcement is applied to another measurable dimension.", sourceHint: "Shaping a behavior within a response topography means that the form of the behavior remains constant, but differential reinforcement is applied to another measurable dimension of the behavior (e.g., magnitude)." },
    { term: "Percentile schedule", definition: "Schedule that reinforces responses above the Nth percentile of recent responses; an efficient tool for shaping.", sourceHint: "Shaping may become more efficient by using... a percentile schedule." },
    { term: "Clicker training", definition: "Science-based system for progressively shaping behavior using positive reinforcement and extinction via a conditioned-reinforcer click.", sourceHint: "Clicker training is a science-based system for progressively shaping behavior using positive reinforcement and extinction." },
  ],
  procedures: [
    {
      title: "Run a shaping program",
      intro: "Apply differential reinforcement of successive approximations.",
      steps: [
        { title: "Specify the terminal behavior", detail: "Define topography, rate, duration, or magnitude criteria." },
        { title: "Analyze the response class", detail: "Identify current behavior and likely approximation steps." },
        { title: "Pick the first approximation", detail: "Choose a current behavior already in the repertoire to reinforce." },
        { title: "Differentially reinforce", detail: "Reinforce the chosen approximation; extinguish others." },
        { title: "Shift criterion gradually", detail: "Move to the next approximation when the current one is stable; limit approximations per level." },
        { title: "Achieve terminal behavior", detail: "Continue reinforcement after the terminal behavior is acquired; maintain via a thinning schedule (Ch 13)." },
      ],
    },
    {
      title: "Increase shaping efficiency",
      intro: "Combine shaping with other tactics.",
      steps: [
        { title: "Add an SD", detail: "Use a clear antecedent to signal when reinforcement is available; fade as needed." },
        { title: "Use prompts", detail: "Physical guidance, modeling, or imitative prompts can speed acquisition; fade them after." },
        { title: "Use percentile schedule", detail: "Reinforce responses above the Nth percentile of recent responses to keep criterion just out of reach." },
      ],
    },
    {
      title: "Conduct clicker training",
      intro: "Pair click with backup reinforcer, then shape.",
      steps: [
        { title: "Establish click as conditioned reinforcer", detail: "Pair the click with a backup reinforcer until clicks alone increase behavior they follow." },
        { title: "Detect approximations", detail: "Click the instant an approximation occurs; deliver backup reinforcer." },
        { title: "Shift criterion", detail: "Move to the next approximation; click only the new, closer responses." },
        { title: "Maintain", detail: "Pair clicks with backups periodically to maintain the conditioned reinforcer function." },
      ],
    },
  ],
  examples: [
    { scenario: "A non-vocal learner is taught to say 'water' starting with any open-mouth sound, then 'wa', then 'wawa', then 'water.'",
      application: "Shaping across response topographies; each approximation is differentially reinforced.",
      sourceHint: "Shaping is defined as the differential reinforcement of successive approximations toward a terminal behavior." },
    { scenario: "A learner walks softly initially; the analyst progressively reinforces louder footfalls until the footsteps are audible.",
      application: "Shaping WITHIN a topography along the dimension of magnitude.",
      sourceHint: "Shaping a behavior within a response topography means that the form of the behavior remains constant, but differential reinforcement is applied to another measurable dimension of the behavior (e.g., magnitude)." },
    { scenario: "Trainer clicks the instant a dog moves its paw toward an object; gradually requires bigger movements; eventually shapes the dog to push the object.",
      application: "Clicker training. Click bridges the response to backup reinforcement.",
      sourceHint: "Clicker training is a science-based system for progressively shaping behavior using positive reinforcement and extinction." },
  ],
  comparisons: [
    {
      title: "Shaping across vs within topographies",
      left:  { label: "Across topographies", points: [
        "Selected response forms reinforced; others extinguished.",
        "Used to shape new forms of behavior.",
      ]},
      right: { label: "Within topography", points: [
        "Form constant; another dimension changes.",
        "Used to shape rate, magnitude, duration, etc.",
      ]},
    },
    {
      title: "Shaping vs Stimulus fading",
      left:  { label: "Shaping", points: [
        "Response changes; SD stays the same.",
        "Differential reinforcement of approximations.",
      ]},
      right: { label: "Stimulus fading", points: [
        "Response stays the same; SD changes gradually.",
        "Used to transfer stimulus control (Ch 17).",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Shaping is just reinforcing everything until the right behavior happens.",
      reality: "Shaping is DIFFERENTIAL reinforcement: closer approximations are reinforced while others are extinguished. Indiscriminate reinforcement does not produce shaping.",
      sourceHint: "Shaping is defined as the differential reinforcement of successive approximations toward a terminal behavior.",
    },
    {
      myth: "Shaping is the same as stimulus fading.",
      reality: "In shaping, the response changes while the SD stays the same. In stimulus fading, the response stays the same while the SD changes.",
      sourceHint: "In shaping, the antecedent stimulus stays the same, while the response progressively becomes more differentiated. In stimulus fading, the response remains the same, while the antecedent stimulus changes gradually.",
    },
    {
      myth: "Shaping is always the most efficient option.",
      reality: "Shaping has limitations; prompting, modeling, or chaining can sometimes teach faster. Assess before adopting shaping.",
      sourceHint: "Shaping has at least five limitations that practitioners should consider before applying it.",
    },
    {
      myth: "Once the terminal behavior is achieved, reinforcement can stop.",
      reality: "Continue reinforcement after the terminal behavior is achieved; use thinning schedules to maintain it.",
      sourceHint: "continue reinforcement when the terminal behavior is achieved.",
    },
  ],
  examChecklist: [
    "Define shaping with all three components.",
    "Define terminal behavior and the dimensions along which behavior can be shaped.",
    "Define differential reinforcement and its three effects.",
    "Define response differentiation and successive approximations.",
    "Distinguish shaping across vs within topographies.",
    "Distinguish shaping from stimulus fading.",
    "List three ways to increase shaping efficiency.",
    "Describe clicker training.",
    "List five shaping guidelines.",
  ],
  flashcardTerms: [
    "Shaping", "Terminal behavior", "Successive approximation",
    "Differential reinforcement", "Response differentiation",
    "Shaping across topographies", "Shaping within a topography",
    "Percentile schedule", "Clicker training",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Shaping is defined as:",
      options: [
        { label: "A", text: "Continuous reinforcement of a single response" },
        { label: "B", text: "Differential reinforcement of successive approximations toward a terminal behavior" },
        { label: "C", text: "Stimulus fading" },
        { label: "D", text: "Extinction of a response class" },
      ],
      answer: "B",
      explanation: "Shaping uses differential reinforcement of successive approximations to build a terminal behavior.",
      topic: "Definition",
      sourceHint: "Shaping is defined as the differential reinforcement of successive approximations toward a terminal behavior.",
    },
    {
      id: "Q2",
      question: "In shaping, the antecedent stimulus:",
      options: [
        { label: "A", text: "Changes gradually while the response stays the same" },
        { label: "B", text: "Stays the same while the response progressively becomes more differentiated" },
        { label: "C", text: "Is replaced by a new stimulus each session" },
        { label: "D", text: "Has no role" },
      ],
      answer: "B",
      explanation: "In shaping, the response changes; the SD stays constant. Stimulus fading is the opposite.",
      topic: "Shaping vs fading",
      sourceHint: "In shaping, the antecedent stimulus stays the same, while the response progressively becomes more differentiated.",
    },
    {
      id: "Q3",
      question: "Differential reinforcement has THREE effects. Which is NOT one of them?",
      options: [
        { label: "A", text: "Responses similar to those that have been reinforced become more likely" },
        { label: "B", text: "Responses resembling unreinforced members are emitted less" },
        { label: "C", text: "Some extinction-burst responses resemble the next approximation and become reinforceable" },
        { label: "D", text: "The SD changes after each approximation" },
      ],
      answer: "D",
      explanation: "The SD does not change during shaping (that is stimulus fading). The three effects involve increases, extinction, and burst responses approximating the next step.",
      topic: "Differential reinforcement",
      sourceHint: "Differential reinforcement has three effects: Responses similar to those that have been reinforced are more likely to occur, responses resembling the unreinforced members are emitted less frequently, and some of these emitted responses under extinction may be likely to resemble the next successive approximation.",
    },
    {
      id: "Q4",
      question: "Clicker training uses the click primarily as a:",
      options: [
        { label: "A", text: "Conditioned reinforcer that bridges the response and backup reinforcer" },
        { label: "B", text: "Punisher to suppress unwanted responses" },
        { label: "C", text: "Discriminative stimulus only" },
        { label: "D", text: "Motivating operation" },
      ],
      answer: "A",
      explanation: "The click is paired with a backup reinforcer until it functions as a conditioned reinforcer, allowing precise differential reinforcement.",
      topic: "Clicker training",
      sourceHint: "Clicker training is a science-based system for progressively shaping behavior using positive reinforcement and extinction. The clicker provides the signal that when a behavior is being performed in the presence of the click, reinforcement follows.",
    },
  ],
  reviewPrompts: [
    "Plan a shaping program for a non-vocal learner approaching the word 'water.'",
    "Distinguish shaping across topographies from shaping within a topography using one example each.",
    "Identify a behavior in your work where shaping would be more efficient than prompting and explain why.",
    "Sketch a clicker-training program for a specific dimension of behavior (e.g., duration of eye contact).",
  ],
  recap: [
    "Shaping is differential reinforcement of successive approximations toward a terminal behavior. The shaper detects changes, judges closeness to terminal behavior, and differentially reinforces approximations while extinguishing others.",
    "Behavior can be shaped along topography, rate, latency, duration, interresponse time, or magnitude. Across-topography shaping changes form; within-topography shaping changes a measurable dimension. Shaping changes the response while the SD stays the same (the opposite of stimulus fading).",
    "Shaping is faster with SDs, prompts, percentile schedules, and conditioned reinforcers such as the clicker. Practitioners should weigh limitations, set clear terminal criteria, proceed gradually, and maintain the terminal behavior after acquisition.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition",
      text: "Shaping is defined as the differential reinforcement of successive approximations toward a terminal behavior. A terminal behavior - the end product of shaping - can be claimed when the topography, rate, latency, duration, interresponse time, or magnitude of the target behavior reaches a predetermined criterion level.",
    },
    {
      heading: "Across vs within topographies",
      text: "Shaping behavior across different response topographies means that selected members of a response class are differentially reinforced, whereas members of other response classes are placed on extinction. Shaping a behavior within a response topography means that the form of the behavior remains constant, but differential reinforcement is applied to another measurable dimension of the behavior (e.g., magnitude).",
    },
    {
      heading: "Shaping vs stimulus fading",
      text: "In shaping, the antecedent stimulus stays the same, while the response progressively becomes more differentiated. In stimulus fading, the response remains the same, while the antecedent stimulus changes gradually.",
    },
  ],
};

export default chapter22Module;
